import { DATE_FORMAT } from '@/helpers/dates';
import { Alert, Box, DateInput } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import { InstallDocumentTypes, QueueStatus } from 'types';

import FormSection from '../Forms/FormSection';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';

import { GeothermalPermitReview } from './GeothermalPermitReview';

const permitDocFilter = [InstallDocumentTypes.permit];
const minDate = DateTime.fromISO('2022-01-01').toJSDate();
const maxDate = DateTime.local().endOf('day').toJSDate();
const dateDirection = { base: 'column' as const, desktop: 'row' as const };
const readOnlyInputProps = { inputProps: { readOnly: true } } as const;
const disabledTextInputProps = readOnlyInputProps;

interface GeothermalPermitSectionProps {
  permitQueueStatus?: QueueStatus;
  isDisabled?: boolean;
  isReviewMode?: boolean;
  isQueueActionDisabled?: boolean;
}

export const GeothermalPermitSection = ({
  permitQueueStatus,
  isDisabled = false,
  isReviewMode = false,
  isQueueActionDisabled = false,
}: GeothermalPermitSectionProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const isApproved = permitQueueStatus === QueueStatus.approved;
  const isRejected = permitQueueStatus === QueueStatus.rejected;

  const handlePermitSubmittedDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!date || !(date instanceof Date)) return;
      setFieldValue('permitSubmittedDate', DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT));
    },
    [setFieldValue],
  );

  const handlePermitApprovedDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!date || !(date instanceof Date)) return;
      setFieldValue('permitApprovedDate', DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT));
    },
    [setFieldValue],
  );

  const handleClearSubmittedDate = useCallback(() => {
    setFieldValue('permitSubmittedDate', '');
  }, [setFieldValue]);

  const handleClearApprovedDate = useCallback(() => {
    setFieldValue('permitApprovedDate', '');
  }, [setFieldValue]);

  const submittedDatePickerProps = useMemo(
    () => ({
      maxDate,
      minDate,
      onChange: handlePermitSubmittedDateChange,
      openToDate: values.permitSubmittedDate
        ? DateTime.fromFormat(values.permitSubmittedDate, DATE_FORMAT).toJSDate()
        : undefined,
    }),
    [handlePermitSubmittedDateChange, values.permitSubmittedDate],
  );

  const approvedDatePickerProps = useMemo(
    () => ({
      maxDate,
      minDate,
      onChange: handlePermitApprovedDateChange,
      openToDate: values.permitApprovedDate
        ? DateTime.fromFormat(values.permitApprovedDate, DATE_FORMAT).toJSDate()
        : undefined,
    }),
    [handlePermitApprovedDateChange, values.permitApprovedDate],
  );

  const submittedTextInputProps = useMemo(
    () => (isDisabled ? disabledTextInputProps : { ...readOnlyInputProps, onClear: handleClearSubmittedDate }),
    [isDisabled, handleClearSubmittedDate],
  );

  const approvedTextInputProps = useMemo(
    () => (isDisabled ? disabledTextInputProps : { ...readOnlyInputProps, onClear: handleClearApprovedDate }),
    [isDisabled, handleClearApprovedDate],
  );

  return (
    <FormSection
      title="Permit"
      description="Permits must be approved before the Loop Field milestone can be completed."
    >
      <Box childGap="lg">
        {isReviewMode && <GeothermalPermitReview isSaving={isQueueActionDisabled} />}
        {isApproved && <Alert variant="success" message="Permit has been approved." hasIcon />}
        {isRejected && (
          <Alert
            variant="danger"
            title="Permit Rejected"
            message="Please upload corrected permit documents and resubmit."
            hasIcon
          />
        )}

        <DocumentList
          documentTypeFilter={permitDocFilter}
          title="Permit Documents"
          showTypeLabelOnItems={false}
          showDocumentActions={!isDisabled}
          showUploadButton={!isDisabled}
          baseCategory={InstallDocumentTypes.permit}
        />
        <Box direction={dateDirection} gap="xl">
          <DateInput
            label="Permit Submitted Date"
            name="permitSubmittedDate"
            id="permitSubmittedDate"
            placeholder="Pick a date"
            dateFormat={DATE_FORMAT}
            isDisabled={isDisabled}
            width="2xl"
            value={values.permitSubmittedDate}
            datePickerProps={submittedDatePickerProps}
            textInputProps={submittedTextInputProps}
          />
          <DateInput
            label="Permit Approved Date"
            name="permitApprovedDate"
            id="permitApprovedDate"
            placeholder="Pick a date"
            dateFormat={DATE_FORMAT}
            isDisabled={isDisabled}
            width="2xl"
            value={values.permitApprovedDate}
            datePickerProps={approvedDatePickerProps}
            textInputProps={approvedTextInputProps}
          />
        </Box>
      </Box>
    </FormSection>
  );
};
